/**
 * Last Minute Deals utilities.
 *
 * Auto-apply discount when booking is made within X hours of the start time.
 *
 * Config stored in listing.publicData.lastMinuteDeal = {
 *   hoursThreshold: number,  — must book within this many hours of start
 *   discountPercent: number  — discount percentage
 * }
 *
 * Example: hoursThreshold: 24, discountPercent: 20
 * → 20% off if booked within 24 hours of start time.
 */

export const DEFAULT_HOURS_THRESHOLD = 24;
export const DEFAULT_DISCOUNT_PERCENT = 15;

/**
 * Get last minute deal config from a listing.
 * @param {Object|null} listing
 * @returns {{ hoursThreshold: number, discountPercent: number } | null}
 */
export const getLastMinuteConfig = (listing) => {
  const config = listing?.attributes?.publicData?.lastMinuteDeal;
  if (!config?.hoursThreshold || !config?.discountPercent) return null;
  return {
    hoursThreshold: parseFloat(config.hoursThreshold),
    discountPercent: parseFloat(config.discountPercent),
  };
};

/**
 * Check if last-minute deal applies.
 * @param {string|Date} bookingStart
 * @param {Object} config — { hoursThreshold }
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isLastMinute = (bookingStart, config, now = new Date()) => {
  if (!config || !bookingStart) return false;
  const start = new Date(bookingStart);
  const hoursUntil = (start.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursUntil >= 0 && hoursUntil <= config.hoursThreshold;
};

/**
 * Calculate last-minute discount.
 * @param {number} priceCents
 * @param {number} discountPercent
 * @returns {{ discountCents: number, finalPriceCents: number }}
 */
export const calculateLastMinuteDiscount = (priceCents, discountPercent) => {
  const discountCents = Math.round(priceCents * (discountPercent / 100));
  return { discountCents, finalPriceCents: priceCents - discountCents };
};
